import React, { useState } from 'react';
import {
  Box, VStack, Heading, Text, Textarea, TextareaInput, Button, ButtonText, Spinner, Alert, Icon, HStack,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, Input, InputField,
  RadioGroup, Radio, RadioIndicator, RadioIcon, RadioLabel, CircleIcon, Divider,
} from '@gluestack-ui/themed';
import { AlertCircle } from 'lucide-react-native';
import { useProcessStage1ContentStudioStage1Post } from '@/common/api/endpoints/content-studio/content-studio';
import { useContentStudioStore } from './store';
import { LoadingOverlay } from './LoadingOverlay';

export const Stage1ConceptCartography = () => {
  const {
    stage1Input, setStage1Input, setStage1Output, setStage, config, setConfig, setStage2Input,
  } = useContentStudioStore();
  const [prompt, setPrompt] = useState(stage1Input.prompt || '');

  const { mutate, isPending, isError } = useProcessStage1ContentStudioStage1Post({
    mutation: {
      onSuccess: (response) => {
        setStage1Output(response.data);

        // Pre-fill answers for Stage 2 (Scope Refinement) from our config
        const initialAnswers = [
          { questionId: 'config_levels', textResponse: String(config.moduleDepth) },
          { questionId: 'config_total_tasks', textResponse: String(config.totalVolume) },
          { questionId: 'config_difficulty', selectedOptionIds: [config.difficulty.toLowerCase()] },
          { questionId: 'config_study_program', textResponse: config.studyProgram },
        ];

        setStage2Input({ answers: initialAnswers });
        setStage(2);
      },
    },
  });

  const handleSubmit = () => {
    if (!prompt.trim()) return;
    setStage1Input({ prompt });
    // Assuming resources is string[] based on check, sending empty array
    mutate({
      data: {
        prompt,
        resources: [],
        config: {
          levels: config.moduleDepth,
          totalTasks: config.totalVolume,
          difficulty: config.difficulty.toLowerCase(),
          studyProgram: config.studyProgram,
        },
      },
    });
  };

  return (
    <Box flex={1} p="$6" maxWidth={800} alignSelf="center" w="100%">
      <LoadingOverlay isLoading={isPending} title="Mapping Concepts" />
      <VStack space="xl">
        <Box>
          <Heading size="xl">What do you want to teach?</Heading>
          <Text color="$textSubtle" mt="$2">
            Describe the topic, learning goals, or target audience. The AI will map out the concepts.
          </Text>
        </Box>

        <Textarea h={160} size="lg" borderColor="$borderLight">
          <TextareaInput
            placeholder="E.g., Introduction to Quantum Computing for High School Students..."
            value={prompt}
            onChangeText={setPrompt}
            p="$4"
          />
        </Textarea>

        <Divider my="$2" />

        <Box>
          <Heading size="md" mb="$4">Structural Constraints</Heading>

          <VStack space="xl">
            {/* Study Program */}
            <VStack space="xs">
              <Text fontWeight="bold" size="sm">Study Program Name</Text>
              <Text size="xs" color="$textSubtle">Helps the AI adapt tone and context.</Text>
              <Input variant="outline" size="md">
                <InputField
                  placeholder="e.g. Corporate Finance"
                  value={config.studyProgram}
                  onChangeText={(val) => setConfig({ studyProgram: val })}
                />
              </Input>
            </VStack>

            <HStack space="xl">
              {/* Module Depth */}
              <VStack space="xs" flex={1}>
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold" size="sm">Module Depth</Text>
                  <Text size="sm" fontWeight="bold" color="$brandBase">
                    {config.moduleDepth}
                    {' '}
                    Levels
                  </Text>
                </HStack>
                <Text size="xs" color="$textSubtle">Number of hierarchy levels.</Text>
                <Slider
                  minValue={2}
                  maxValue={10}
                  step={1}
                  value={config.moduleDepth}
                  onChange={(val) => setConfig({ moduleDepth: val })}
                  mt="$2"
                >
                  <SliderTrack>
                    <SliderFilledTrack bg="$brandBase" />
                  </SliderTrack>
                  <SliderThumb bg="$brandBase" />
                </Slider>
              </VStack>

              {/* Total Volume */}
              <VStack space="xs" flex={1}>
                <Text fontWeight="bold" size="sm">Total Task Volume</Text>
                <Text size="xs" color="$textSubtle">Total essay tasks in the module.</Text>
                <RadioGroup
                  value={String(config.totalVolume)}
                  onChange={(val) => setConfig({ totalVolume: Number(val) })}
                >
                  <HStack space="md" flexWrap="wrap">
                    {[3, 6, 9, 12, 15].map((v) => (
                      <Radio key={v} value={String(v)} size="sm">
                        <RadioIndicator mr="$1">
                          <RadioIcon as={CircleIcon} />
                        </RadioIndicator>
                        <RadioLabel>{v}</RadioLabel>
                      </Radio>
                    ))}
                  </HStack>
                </RadioGroup>
              </VStack>
            </HStack>

            {/* Difficulty Level */}
            <VStack space="xs">
              <Text fontWeight="bold" size="sm">Difficulty Level</Text>
              <RadioGroup
                value={config.difficulty}
                onChange={(val) => setConfig({ difficulty: val as 'Bachelor' | 'Master' | 'Professional' })}
              >
                <HStack space="xl">
                  {['Bachelor', 'Master', 'Professional'].map((d) => (
                    <Radio key={d} value={d} size="md">
                      <RadioIndicator mr="$2">
                        <RadioIcon as={CircleIcon} />
                      </RadioIndicator>
                      <RadioLabel>{d}</RadioLabel>
                    </Radio>
                  ))}
                </HStack>
              </RadioGroup>
            </VStack>
          </VStack>
        </Box>

        {isError && (
          <Alert action="error" variant="solid" borderRadius="$md">
            <Icon as={AlertCircle} mr="$3" color="$textOnError" />
            <Text color="$textOnError">Failed to process request. Please try again.</Text>
          </Alert>
        )}

        <Button
          onPress={handleSubmit}
          isDisabled={!prompt.trim() || isPending}
          size="lg"
          mt="$4"
        >
          {isPending && <Spinner color="$white" mr="$2" />}
          <ButtonText>{isPending ? 'Mapping Concepts...' : 'Generate Concept Map'}</ButtonText>
        </Button>
      </VStack>
    </Box>
  );
};
